import {
    ISSUE_VOUCHER_LIMITS,
    IssueVouchersCommand,
} from '../model/issuedVoucher.types';

/** Raw form state of the issue voucher form (inputs are kept as strings). */
export type IssueVoucherFormValues = {
    voucherTemplateId: string;
    amount: string;
    quantity: string;
    validUntil: string;
    sender: string;
    receiver: string;
    text: string;
    note: string;
};

export type IssueVoucherFormErrors = Partial<
    Record<keyof IssueVoucherFormValues, string>
>;

export const createEmptyIssueVoucherForm = (): IssueVoucherFormValues => ({
    voucherTemplateId: '',
    amount: '',
    quantity: '1',
    validUntil: '',
    sender: '',
    receiver: '',
    text: '',
    note: '',
});

/** Today as YYYY-MM-DD in the local time zone of the browser. */
export const todayAsIsoDate = (now: Date = new Date()): string => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseInteger = (value: string): number | null => {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    return Number(trimmed);
};

const { minAmount, maxAmount, minQuantity, maxQuantity } = ISSUE_VOUCHER_LIMITS;

/**
 * Validates the amount on its own; the preview needs a valid amount as well.
 */
export const validateIssueVoucherAmount = (amount: string): string | null => {
    if (!amount.trim()) return 'Bitte einen Betrag angeben.';
    const value = parseInteger(amount);
    if (value === null) return 'Nur ganze Euro-Beträge sind möglich.';
    if (value < minAmount) return `Mindestens ${minAmount} €.`;
    if (value > maxAmount)
        return `Höchstens ${maxAmount.toLocaleString('de-DE')} €.`;
    return null;
};

/**
 * Client-side mirror of the server rules of POST /admin/issued-vouchers.
 * The server stays authoritative; this only gives early feedback.
 */
export const validateIssueVoucherForm = (
    values: IssueVoucherFormValues,
    today: string = todayAsIsoDate()
): IssueVoucherFormErrors => {
    const errors: IssueVoucherFormErrors = {};
    const limits = ISSUE_VOUCHER_LIMITS;

    if (!values.voucherTemplateId) {
        errors.voucherTemplateId = 'Bitte eine Vorlage wählen.';
    }

    const amountError = validateIssueVoucherAmount(values.amount);
    if (amountError) errors.amount = amountError;

    const quantity = parseInteger(values.quantity);
    if (quantity === null || quantity < minQuantity || quantity > maxQuantity) {
        errors.quantity = `Anzahl zwischen ${minQuantity} und ${maxQuantity}.`;
    }

    if (values.validUntil) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(values.validUntil)) {
            errors.validUntil = 'Ungültiges Datum.';
        } else if (values.validUntil < today) {
            errors.validUntil =
                'Das Datum darf nicht in der Vergangenheit liegen.';
        }
    }

    if (values.sender.length > limits.maxNameLength) {
        errors.sender = `Höchstens ${limits.maxNameLength} Zeichen.`;
    }
    if (values.receiver.length > limits.maxNameLength) {
        errors.receiver = `Höchstens ${limits.maxNameLength} Zeichen.`;
    }
    if (values.text.length > limits.maxTextLength) {
        errors.text = `Höchstens ${limits.maxTextLength} Zeichen.`;
    }

    if (!values.note.trim()) {
        errors.note = 'Bitte einen Grund angeben.';
    } else if (values.note.length > limits.maxNoteLength) {
        errors.note = `Höchstens ${limits.maxNoteLength} Zeichen.`;
    }

    return errors;
};

/** Builds the request body; call only with a form that passed validation. */
export const toIssueVouchersCommand = (
    values: IssueVoucherFormValues
): IssueVouchersCommand => ({
    voucherTemplateId: values.voucherTemplateId,
    amount: { amount: Number(values.amount.trim()), currency: 'EUR' },
    quantity: Number(values.quantity.trim()),
    validUntil: values.validUntil || null,
    sender: values.sender,
    receiver: values.receiver,
    text: values.text,
    note: values.note.trim(),
});

const ISSUE_VOUCHER_ERROR_MESSAGES: Record<string, string> = {
    voucher_tool_disabled:
        'Das Gutscheintool ist für diese Bergbahn nicht aktiviert.',
    lift_operator_not_found: 'Die Bergbahn wurde nicht gefunden.',
    forbidden: 'Keine Berechtigung, Gutscheine auszustellen.',
    voucher_template_not_found:
        'Die Vorlage ist nicht (mehr) aktiv oder unvollständig. Bitte eine andere Vorlage wählen.',
    min: `Der Betrag muss mindestens ${minAmount} € betragen.`,
    max: `Der Betrag darf höchstens ${maxAmount.toLocaleString('de-DE')} € betragen.`,
    invalid_amount: 'Der Betrag muss ein ganzer Euro-Betrag sein.',
    invalid_currency: 'Es sind nur Beträge in Euro möglich.',
    invalid_quantity: `Die Anzahl muss zwischen ${minQuantity} und ${maxQuantity} liegen.`,
    invalid_valid_until:
        'Das Gültigkeitsdatum darf nicht in der Vergangenheit liegen.',
    note_required: 'Bitte eine interne Notiz bzw. einen Grund angeben.',
};

export const ISSUE_VOUCHER_DEFAULT_ERROR =
    'Fehler beim Ausstellen. Bitte später noch einmal versuchen.';

/** Extracts the backend error code (`errors[0].error`) of an RTK Query error. */
export const getBackendErrorCode = (err: unknown): string | undefined =>
    (err as { data?: { errors?: { error?: string }[] } })?.data?.errors?.[0]
        ?.error;

/** Maps a backend error of the issue endpoints to a German message. */
export const getIssueVoucherErrorMessage = (
    err: unknown,
    fallback: string = ISSUE_VOUCHER_DEFAULT_ERROR
): string => {
    const code = getBackendErrorCode(err);
    return (code && ISSUE_VOUCHER_ERROR_MESSAGES[code]) || fallback;
};

/**
 * Display name for the issuer column: the main account, the username of a
 * sub-user, or "gelöschter Benutzer" for an id that is no longer in the user
 * list. Without a loaded user list the raw id is shown as a fallback.
 */
export const formatIssuer = (
    issuedBy: string | null | undefined,
    liftOperatorId: string | undefined,
    issuerNames?: Record<string, string>
): string => {
    if (!issuedBy) return '-';
    if (issuedBy === liftOperatorId) return 'Hauptkonto';
    if (!issuerNames) return issuedBy;
    return issuerNames[issuedBy] ?? 'gelöschter Benutzer';
};

/**
 * RTK Query keeps the last successful result in `data` when a query with new
 * arguments fails. Without this guard the table would show the vouchers of the
 * previous search or page under the new search term or page number.
 */
export const resultOfCurrentQuery = <T>(
    data: T | undefined,
    error: unknown
): T | undefined => (error ? undefined : data);
